import React, { useEffect, useState } from 'react';
import { useAlert } from './contexts/AlertContext';
import { useAuth } from './contexts/AuthContext';
import apiService from './apiService';
import { TrendingUp, CheckSquare } from 'lucide-react';

const UserAdmin = ({ onNavigate }) => {
    const { currentUser } = useAuth();
    const { showAlert } = useAlert();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [invite, setInvite] = useState({ email: '', role: 'receptionist' });

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const list = await apiService.getUsers({ organizationId: currentUser.organizationId });
                if (mounted) setUsers(list);
            } catch (e) {
                showAlert('Failed to load users', 'error');
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [currentUser, showAlert]);

    const handleInvite = async (e) => {
        e.preventDefault();
        try {
            await apiService.inviteUser({ email: invite.email, role: invite.role, organizationId: currentUser.organizationId });
            showAlert('Invitation sent', 'success');
            setInvite({ email: '', role: 'receptionist' });
        } catch (e) {
            showAlert('Failed to invite user', 'error');
        }
    };

    if (!['org_admin'].includes(currentUser?.role)) {
        return <div className="p-6 text-red-600">Access denied.</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold">Administration</h2>
                    <p className="text-gray-600 dark:text-gray-400">Manage users, roles, and organization settings</p>
                </div>
                <div className="flex gap-2">
                    {onNavigate && (
                        <>
                            <button
                                onClick={() => onNavigate('upgrade')}
                                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
                            >
                                <TrendingUp className="w-4 h-4" />
                                Upgrade Modules
                            </button>
                            <button
                                onClick={() => onNavigate('bulk-operations')}
                                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center gap-2"
                            >
                                <CheckSquare className="w-4 h-4" />
                                Bulk Operations
                            </button>
                        </>
                    )}
                </div>
            </div>
            <form onSubmit={handleInvite} className="bg-white dark:bg-gray-800 p-4 rounded-lg space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input type="email" required placeholder="user@example.com" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} className="border p-2 rounded" />
                    <select value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })} className="border p-2 rounded">
                        <option value="receptionist">Receptionist</option>
                        <option value="doctor">Doctor</option>
                        <option value="nurse">Nurse</option>
                        <option value="pharmacist">Pharmacist</option>
                        <option value="cashier">Cashier</option>
                        <option value="org_admin">Org Admin</option>
                    </select>
                    <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded">Invite</button>
                </div>
            </form>
            <div className="bg-white dark:bg-gray-800 rounded-lg">
                {loading ? (
                    <div className="p-6">Loading users…</div>
                ) : (
                    <table className="w-full text-left">
                        <thead>
                            <tr className="border-b">
                                <th className="p-3">Email</th>
                                <th className="p-3">Role</th>
                                <th className="p-3">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(u => (
                                <tr key={u.id} className="border-b">
                                    <td className="p-3">{u.email || u.username}</td>
                                    <td className="p-3">{u.role}</td>
                                    <td className="p-3">{u.is_active ? 'Active' : 'Disabled'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default UserAdmin;


